import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import AnimatedPage from '../components/AnimatedPage';
import ReusableModal from '../components/ReusableModal';
import { gsap } from "gsap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeMute, faVolumeUp, faPause, faPlay } from '@fortawesome/free-solid-svg-icons';

const pageTransitionDuration = 0.5;

const fadeIn = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: pageTransitionDuration,
      // delay: pageTransitionDuration,  // optional delay, comment if unwanted
    }
  },
  exit: {
    opacity: 1,
  }
}

export default function Page({ page, isMuted, setIsMuted }) {
  const thisRef = useRef();
  const timeoutRef = useRef();
  const [interactionsWidth, setInteractionsWidth] = useState('0px');
  const [interactionsHeight, setInteractionsHeight] = useState('0px');
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // mute and unmute
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };
  // end mute and unmute

  useEffect(() => {
    setIsActive(true);

    // calculate interactions size on load
    const sixteenNineAspect = 16 / 9;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const widthIfConstarainedByHeight = viewportHeight * sixteenNineAspect;
    const heightIfConstarainedByWidth = viewportWidth / sixteenNineAspect;
    const videoTransitionIn = thisRef.current.querySelector('.video-transition-in');
    const videoBgLoop = thisRef.current.querySelector('.video-bg-loop');
    let videoTime = 0;

    if (widthIfConstarainedByHeight <= viewportWidth) {
      setInteractionsWidth(widthIfConstarainedByHeight + "px");
      setInteractionsHeight(viewportHeight + "px");
    } else {
      setInteractionsWidth(viewportWidth + "px");
      setInteractionsHeight(heightIfConstarainedByWidth + "px");
    }

    const resizeFunctions = () => {
      const sixteenNineAspect = 16 / 9;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const widthIfConstarainedByHeight = viewportHeight * sixteenNineAspect;
      const heightIfConstarainedByWidth = viewportWidth / sixteenNineAspect;

      if (widthIfConstarainedByHeight <= viewportWidth) {
        setInteractionsWidth(widthIfConstarainedByHeight + "px");
        setInteractionsHeight(viewportHeight + "px");
      } else {
        setInteractionsWidth(viewportWidth + "px");
        setInteractionsHeight(heightIfConstarainedByWidth + "px");
      }
    };

    window.addEventListener('resize', resizeFunctions);
    // end calculate interactions

    // play videos
    const pauseTransitionIn = () => {
      if (videoTransitionIn.paused) {
        videoTransitionIn.play();
        setIsPaused(false);
      } else {
        videoTransitionIn.pause();
        setIsPaused(true);
      }
    };
    
    const playTransitionIn = async () => {
      try {
        await videoTransitionIn.play();
        if (page.pausable) {
          thisRef.current.querySelector('.pause').addEventListener('click', pauseTransitionIn);
        }
      } catch (error) {
        console.log(error);
      }
    }

    const pauseBgLoop = () => {
      if (videoBgLoop.paused) {
        videoBgLoop.play();
        setIsPaused(false);
      } else {
        videoBgLoop.pause();
        setIsPaused(true);
      }
    }

    const playBgLoop = async () => {
      try {
        videoBgLoop.classList.remove('d-none');
        await videoBgLoop.play();
        if (page.pausable) {
          thisRef.current.querySelector('.pause').addEventListener('click', pauseBgLoop);
        }
      } catch (error) {
        console.log(error);
      }
    }

    let dissappearingCtas = thisRef.current.querySelectorAll('[data-dissappear-after]');
    let appearingCtas = thisRef.current.querySelectorAll('[data-appear-after]');

    const handleAppearAfterAndDissappearAfter = () => {
      dissappearingCtas.forEach(cta => {
        if (cta.getAttribute('data-dissappear-actioned') === 'true') return;

        const dissappearAfter = parseFloat(cta.getAttribute('data-dissappear-after'));
        if (videoTime >= dissappearAfter) {
          cta.classList.add('d-none');
          cta.setAttribute('data-dissappear-actioned', 'true');
        }
      });
    
      appearingCtas.forEach(cta => {
        if (cta.getAttribute('data-appear-actioned') === 'true') return;
        
        const appearAfter = parseFloat(cta.getAttribute('data-appear-after'));
        console.log(videoTime, appearAfter, videoTime >= appearAfter);
        if (videoTime >= appearAfter) {
          cta.classList.remove('d-none');
          cta.setAttribute('data-appear-actioned', 'true');
        }
      });
    };

    const handleAppearAfterAndDissappearAfterEnd = () => {
      dissappearingCtas.forEach(cta => {
        if (cta.getAttribute('data-dissappear-actioned') === 'true') return;
        cta.classList.add('d-none');
        cta.setAttribute('data-dissappear-actioned', 'true');
      });
    
      appearingCtas.forEach(cta => {
        if (cta.getAttribute('data-appear-actioned') === 'true') return;
        cta.classList.remove('d-none');
        cta.setAttribute('data-appear-actioned', 'true');
      });
    };

    const updateVideoTransitionInTime = () => {
      videoTime = videoTransitionIn.currentTime;
      handleAppearAfterAndDissappearAfter();
    };

    const updateVideoBgLoopTime = () => {
      videoTime = videoBgLoop.currentTime;
      handleAppearAfterAndDissappearAfter();
    };

    if (page.videoBgLoop !== "") {
      if (!!videoBgLoop) {
        if (!!videoTransitionIn) {
          videoTransitionIn.addEventListener('ended', playBgLoop);
        }
      }
    } else {
      let videoTransitionInEnd = thisRef.current.querySelector('.video-transition-in-end');

      if (!!videoTransitionInEnd) {
        const showTransitionInEnd = async () => {
          videoTransitionInEnd.style.opacity = 1;
        }

        videoTransitionIn.addEventListener('ended', showTransitionInEnd);
      }
    }

    if (isActive) {
      if (!!videoTransitionIn) {
        playTransitionIn();
        videoTransitionIn.addEventListener('timeupdate', updateVideoTransitionInTime);
        videoTransitionIn.addEventListener('ended', handleAppearAfterAndDissappearAfterEnd);
      } else {
        playBgLoop();
        videoBgLoop.addEventListener('timeupdate', updateVideoBgLoopTime);
        videoBgLoop.addEventListener('ended', handleAppearAfterAndDissappearAfterEnd);
      }
    };
    // end play videos

    // navigation
    const autoNavigateUrl = thisRef.current.querySelector('[data-auto-navigate]');

    // auto navigate after video ends
    const autoNavigate = async () => {
      const url = autoNavigateUrl.dataset.autoNavigate;
      navigate(url, { state: { transition: 0 } });
    }

    if (!!autoNavigateUrl) {
      videoTransitionIn.addEventListener('ended', autoNavigate);
    }

    // basic navigation
    const ctx = gsap.context(() => {
      const bg2 = thisRef.current.querySelector('.bg-2');
      const tween = gsap.to(bg2, {
        opacity: 1,
        duration: pageTransitionDuration,
        paused: true
      });

      const navTo = (e) => {
        if (!!autoNavigateUrl) {
          videoTransitionIn.removeEventListener('ended', autoNavigate);
        }
        const target = e.target.classList.contains('nav-btn') ? e.target : e.target.parentElement;
        const navToRoute = target.dataset.route;

        timeoutRef.current = setTimeout(() => {
          tween.play(0, false);
          navigate(navToRoute, { state: { transition: 0 } });
        }, pageTransitionDuration * 1000);
      };

      const navBtns = thisRef.current.querySelectorAll('.nav-btn');

      navBtns.forEach(navBtn => {
        navBtn.addEventListener('click', navTo);
      });
    });
    // end basic navigation
    // end navigation

    // Unmount -> cleanup
    return () => {
      setIsActive(false);
      ctx.revert();
      clearTimeout(timeoutRef.current);
      window.removeEventListener('resize', resizeFunctions);
      if (!!autoNavigateUrl) {
        videoTransitionIn.removeEventListener('ended', autoNavigate);
      }
    };
  }, [navigate, page.videoBgLoop, isActive, page.pausable]);

  return (
    <AnimatedPage
      ref={thisRef}
    >
      <main
        className="d-flex flex-grow-1 flex-column justify-content-center align-items-center bg-black overflow-hidden"
      >
        <div className="ratio ratio-16x9 mh-100vh overflow-hidden">
          <img
            src={page.bgImage.default}
            style={{ objectFit: 'contain' }}
            alt="background"
          />
          <motion.video
            variants={fadeIn}
            initial="initial"
            animate="animate"
            exit="exit"
            src={page.videoTransitionIn.default}
            type="video/mp4"
            className="video-transition-in w-100"
            muted={isMuted}
            {...(page.autoNavigateURL != null && { 'data-auto-navigate': page.autoNavigateURL })}
          />
          {page.videoBgLoop !== "" ? (
            <video
              src={page.videoBgLoop.default}
              type="video/mp4"
              className="video-bg-loop d-none w-100"
              muted={isMuted}
              loop
            />
          ) : (
            <img
              src={page.videoTransitionInEnd.default}
              className="video-transition-in-end"
              style={{ objectFit: 'contain', opacity: 0 }}
              alt="background"
            />
          )}
          <img
            className="bg-2"
            src={page.bgImage2.default}
            style={{ objectFit: 'contain', opacity: 0 }}
            alt="background"
          />
          <div
            className="interactions overflow-hidden"
            style={{ width: interactionsWidth, height: interactionsHeight }}
          >
            {page.pausable && (
              <FontAwesomeIcon
                icon={isPaused ? faPlay : faPause}
                className="pause position-absolute top-0 start-0"
                style={{ cursor: 'pointer', fontSize: `calc(0.025 * ${interactionsHeight})`, color: 'white', right: '50px', padding: '2%' }}
              />
            )}

            {/* <FontAwesomeIcon
              icon={isMuted ? faVolumeMute : faVolumeUp}
              className="position-absolute top-0 end-0"
              onClick={toggleMute}
              style={{ cursor: 'pointer', fontSize: `calc(0.025 * ${interactionsHeight})`, color: 'white', padding: '2%' }}
            /> */}

            {page.ctas.map((cta, index) => {
              return cta.modal ? (
                <ReusableModal
                  key={index}
                  width={cta.width}
                  height={cta.height}
                  top={cta.top}
                  left={cta.left}
                  interactionsWidth={interactionsWidth}
                  interactionsHeight={interactionsHeight}
                >
                  {cta.type === 'video' ? (
                    <video
                      src={cta.media.default}
                      type="video/mp4"
                      className="modal-video w-100"
                      muted={isMuted}
                      loop
                      disablePictureInPicture
                      {...(cta.pausable && { 'data-pausable': cta.pausable })}
                    />
                  ) : (
                    <img
                      src={cta.media.default}
                      alt={cta.alt}
                      className="w-100"
                    />
                  )}
                </ReusableModal>
              ) : (
                <div
                  key={index}
                  className={`position-absolute nav-btn ${cta.appearAfter != null ? 'd-none' : ''}`}
                  style={{
                    width: cta.width,
                    height: cta.height,
                    top: cta.top,
                    left: cta.left,
                    cursor: 'pointer',
                    ...(cta.transform && { transform: cta.transform })
                  }}
                  data-route={cta.url}
                  {...(cta.dissappearAfter != null && { 'data-dissappear-after': cta.dissappearAfter })}
                  {...(cta.appearAfter != null && { 'data-appear-after': cta.appearAfter })}
                ></div>
              );
            })}

          </div>
        </div>
      </main>
    </AnimatedPage>
  )
}